/**
 * BulkCommentModal Component
 * Modal for adding the same comment to multiple selected powers.
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { OathModal, OathButton, OathAlert } from '../../../shared/OathUI';
import {
  createBulkComment,
  COMMENT_MAX_LENGTH,
  COMMENT_WARNING_THRESHOLD,
} from '../../../../services/oathPowerCommentService';

const BulkCommentModal = ({ isOpen, onClose, selectedPowers = new Set(), onSuccess }) => {
  const [commentText, setCommentText] = useState('');
  const [contactAgent, setContactAgent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const powerNumbers = Array.from(selectedPowers);
  const charCount = commentText.length;
  const showCounter = charCount > COMMENT_WARNING_THRESHOLD;
  const isNearLimit = charCount > 3900;
  const isAtLimit = charCount >= COMMENT_MAX_LENGTH;
  const canSubmit = commentText.trim().length > 0 && !isAtLimit && !isSubmitting && powerNumbers.length > 0;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCommentText('');
      setContactAgent(false);
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const result = await createBulkComment({
      powerNumbers,
      comment: commentText.trim(),
      contactAgent,
    });

    if (result.success) {
      const { count, requestedCount } = result;
      if (count < requestedCount) {
        toast.warning(`Comment added to ${count} of ${requestedCount} powers. Some powers were not accessible.`);
      } else {
        toast.success(`Comment added to ${count} power${count !== 1 ? 's' : ''}`);
      }
      onSuccess?.();
      onClose();
    } else {
      setSubmitError(result.error || 'Failed to add comments');
    }

    setIsSubmitting(false);
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add Comment to ${powerNumbers.length} Power${powerNumbers.length !== 1 ? 's' : ''}`}
      size="md"
      footer={
        <>
          <OathButton variant="outline" size="md" onClick={onClose} disabled={isSubmitting} className="flex-1 sm:flex-none">
            Cancel
          </OathButton>
          <OathButton variant="primary" size="md" onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit} className="flex-1 sm:flex-none">
            Add Comment
          </OathButton>
        </>
      }
    >
      <div className="space-y-4">
        {/* Selected powers */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Selected Powers
          </label>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            {powerNumbers.map(pn => (
              <span
                key={pn}
                className="inline-flex items-center px-2 py-0.5 text-xs font-mono font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded"
              >
                {pn}
              </span>
            ))}
          </div>
        </div>

        {/* Comment textarea */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Comment
          </label>
          <textarea
            value={commentText}
            onChange={(e) => {
              if (e.target.value.length <= COMMENT_MAX_LENGTH) {
                setCommentText(e.target.value);
              }
            }}
            placeholder="Write a comment that will be added to all selected powers..."
            rows={4}
            className="w-full px-4 py-3 border rounded-xl text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 border-gray-300 dark:border-gray-600 resize-none"
            disabled={isSubmitting}
          />
          {showCounter && (
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${isNearLimit ? 'text-red-500 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                {charCount.toLocaleString()} / {COMMENT_MAX_LENGTH.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Contact agent checkbox */}
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={contactAgent}
            onChange={(e) => setContactAgent(e.target.checked)}
            disabled={isSubmitting}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/30"
          />
          <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Contact agent <span className="text-gray-400 dark:text-gray-500">(notifies Bankers Surety via email)</span>
          </span>
        </label>

        {contactAgent && (
          <OathAlert variant="info" message="An email notification will be sent to Bankers Surety for each region associated with the selected powers." />
        )}

        {/* Submit error */}
        {submitError && (
          <OathAlert variant="error" message={submitError} />
        )}
      </div>
    </OathModal>
  );
};

export default BulkCommentModal;
